/**
 * CoWork chat sync — persistence glue (hybrid CoWork). When a CoWork chat is
 * (re)opened it restores that chat's saved activity and auto-reconnects the
 * device to the folder the chat worked in (driving a loading state), so files,
 * folders and activity are all there when you come back. It also persists the
 * chat's activity as it changes. Desktop-only; renders nothing.
 *
 * @module components/cowork/cowork-chat-sync
 */

import { useEffect } from "react";

import { isDesktopApp } from "@/lib/desktop";
import { useCowork } from "@/hooks/use-cowork";
import { useCoworkStore } from "@/store/cowork-store";

export function CoworkChatSync({ chatId }: { chatId: string }) {
  const { openRecent } = useCowork();
  const changes = useCoworkStore((s) => s.changes);
  const desktop = isDesktopApp();

  // On (re)open: restore this chat's activity + reconnect its folder.
  useEffect(() => {
    if (!desktop || !chatId) return;
    const store = useCoworkStore.getState();
    if (!store.coworkChatIds.includes(chatId)) return; // not a CoWork chat

    // A CoWork chat is open — make sure we're in CoWork mode. After a hard
    // reload `mode` defaults to "chat", which would route this chat to the
    // cloud instead of the device; restoring it here keeps things consistent.
    if (store.mode !== "cowork") store.setMode("cowork");

    store.restoreChatActivity(chatId);

    const folder = store.getChatFolder(chatId);
    if (folder && folder !== store.folderPath) {
      store.setReconnecting(true);
      void openRecent(folder).finally(() => useCoworkStore.getState().setReconnecting(false));
    }
  }, [chatId, desktop, openRecent]);

  // Persist activity as it changes (only for CoWork chats).
  useEffect(() => {
    if (!desktop || !chatId) return;
    if (!useCoworkStore.getState().coworkChatIds.includes(chatId)) return;
    useCoworkStore.getState().saveChatActivity(chatId, changes);
  }, [changes, chatId, desktop]);

  return null;
}
